import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

import { useProjectStore, PublishStatus, BuildStatus } from '@/stores/projectStore';
import { auth } from '@/lib/firebase';
import { deploymentsApi } from '@/lib/api/deployments';
import { Deployment } from '@/types/deployment';

const API_URL = import.meta.env.VITE_API_URL;

interface UsePublishOptions {
  projectId?: string;
  onSuccess?: (publicUrl: string) => void;
  onError?: (error: string) => void;
}

interface UsePublishReturn {
  // State from store
  publishState: {
    status: PublishStatus;
    buildId: string | null;
    buildStatus: BuildStatus | null;
    error: string | null;
    publicUrl: string | null;
    startedAt: number | null;
    fastPublish: boolean;
  };
  // Computed states
  isPublishing: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  elapsedTime: number;
  // Actions
  triggerPublish: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook for managing publish state across the app.
 * Uses Zustand store for state synchronization between PublishDropdown and HITL card.
 */
export function usePublish({
  projectId,
  onSuccess,
  onError,
}: UsePublishOptions = {}): UsePublishReturn {
  const {
    publishState,
    setPublishState,
    resetPublishState,
    setIsPublishing,
    setCurrentProject,
    currentProject,
  } = useProjectStore();

  // Refs for polling intervals
  const buildPollRef = useRef<NodeJS.Timeout | null>(null);
  const deployPollRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedTimeRef = useRef(0);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (buildPollRef.current) clearInterval(buildPollRef.current);
      if (deployPollRef.current) clearInterval(deployPollRef.current);
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, []);

  // Poll for build status
  const pollBuildStatus = useCallback(async (buildId: string) => {
    if (!projectId) return;

    try {
      const token = await auth.getIdToken();
      const response = await axios.get(
        `${API_URL}/api/projects/${projectId}/builds/${buildId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { status, message } = response.data;
      setPublishState({ buildStatus: status });

      if (status === 'failed' || status === 'timeout') {
        // Build failed
        if (buildPollRef.current) clearInterval(buildPollRef.current);
        if (elapsedRef.current) clearInterval(elapsedRef.current);

        const errorMsg = message || 'Build failed';
        setPublishState({
          status: 'failed',
          error: errorMsg,
          buildId: null
        });
        setIsPublishing(false);
        onError?.(errorMsg);

        // Reset after 5 seconds
        setTimeout(() => {
          resetPublishState();
        }, 5000);
      } else if (status === 'success') {
        // Build succeeded, now poll for deployment
        if (buildPollRef.current) clearInterval(buildPollRef.current);
        setPublishState({
          status: 'deploying',
          buildId: null,
          buildStatus: null
        });
        pollForDeployment();
      }
    } catch (error) {
      console.error('Error polling build status:', error);
    }
  }, [projectId, setPublishState, setIsPublishing, onError, resetPublishState]);

  // Poll for deployment
  const pollForDeployment = useCallback(async () => {
    if (!projectId) return;

    // Wait a bit for auto-deploy to start
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const deployments = await deploymentsApi.getDeployments(projectId, 1, 'publish');
      if (deployments.length === 0) {
        // No deployment yet, keep polling
        deployPollRef.current = setTimeout(() => pollForDeployment(), 3000);
        return;
      }

      const latestDeployment = deployments[0];

      if (latestDeployment.status === 'success') {
        // Deployment succeeded!
        if (elapsedRef.current) clearInterval(elapsedRef.current);

        setPublishState({
          status: 'success',
          publicUrl: latestDeployment.publicUrl || null,
        });
        setIsPublishing(false);

        // Update project store with new URL
        if (latestDeployment.publicUrl && currentProject) {
          setCurrentProject({
            ...currentProject,
            latestPublishUrl: latestDeployment.publicUrl,
          });
        }

        onSuccess?.(latestDeployment.publicUrl || '');

        // Show success toast
        toast.success(
          <div className="flex flex-col gap-0.5 min-w-0 max-w-[250px]">
            <span className="text-sm font-medium">Published!</span>
            {latestDeployment.publicUrl && (
              <a
                href={latestDeployment.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 underline block truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {latestDeployment.publicUrl}
              </a>
            )}
          </div>,
          { duration: 8000 }
        );
      } else if (latestDeployment.status === 'failed') {
        // Deployment failed
        if (elapsedRef.current) clearInterval(elapsedRef.current);

        setPublishState({
          status: 'failed',
          error: 'Deployment failed',
        });
        setIsPublishing(false);
        onError?.('Deployment failed');

        toast.error('Deployment failed', { duration: 4000 });

        // Reset after 5 seconds
        setTimeout(() => {
          resetPublishState();
        }, 5000);
      } else {
        // Still deploying, keep polling
        deployPollRef.current = setTimeout(() => pollForDeployment(), 3000);
      }
    } catch (error) {
      console.error('Error polling deployment:', error);
      deployPollRef.current = setTimeout(() => pollForDeployment(), 3000);
    }
  }, [projectId, setPublishState, setIsPublishing, currentProject, setCurrentProject, onSuccess, onError, resetPublishState]);

  // Main publish function
  const triggerPublish = useCallback(async () => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    // Reset and start
    elapsedTimeRef.current = 0;
    setPublishState({
      status: 'queued',
      buildId: null,
      buildStatus: 'queued',
      error: null,
      publicUrl: null,
      startedAt: Date.now(),
      fastPublish: false,
      projectId, // Track which project this publish belongs to
    });
    setIsPublishing(true);

    // Start elapsed time counter
    elapsedRef.current = setInterval(() => {
      elapsedTimeRef.current += 1;
    }, 1000);

    try {
      const token = await auth.getIdToken();

      const response = await axios.post(
        `${API_URL}/api/projects/${projectId}/builds/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { fastPublish, buildId, message } = response.data;
      console.log('[usePublish] Response:', response.data);

      if (fastPublish) {
        // Fast publish - done immediately
        console.log('[usePublish] Fast publish completed:', message);
        if (elapsedRef.current) clearInterval(elapsedRef.current);

        setPublishState({
          status: 'deploying',
          fastPublish: true,
        });

        // Poll for deployment to get the URL
        pollForDeployment();
      } else {
        // Full build started - poll for status
        console.log('[usePublish] Full build started:', message);
        setPublishState({
          status: 'building',
          buildId,
          buildStatus: 'running',
        });

        // Start polling for build status
        buildPollRef.current = setInterval(() => {
          pollBuildStatus(buildId);
        }, 2000);

        // Initial poll
        pollBuildStatus(buildId);
      }
    } catch (error: any) {
      if (elapsedRef.current) clearInterval(elapsedRef.current);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to start publish';

      setPublishState({
        status: 'failed',
        error: errorMessage,
      });
      setIsPublishing(false);
      onError?.(errorMessage);

      toast.error(errorMessage);

      // Reset after 5 seconds
      setTimeout(() => {
        resetPublishState();
      }, 5000);
    }
  }, [projectId, setPublishState, setIsPublishing, pollBuildStatus, pollForDeployment, onError, resetPublishState]);

  // Reset function
  const reset = useCallback(() => {
    if (buildPollRef.current) clearInterval(buildPollRef.current);
    if (deployPollRef.current) clearInterval(deployPollRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    elapsedTimeRef.current = 0;
    resetPublishState();
  }, [resetPublishState]);

  // Computed values
  const isPublishing = publishState.status !== 'idle' && publishState.status !== 'success' && publishState.status !== 'failed';
  const isSuccess = publishState.status === 'success';
  const isFailed = publishState.status === 'failed';

  return {
    publishState,
    isPublishing,
    isSuccess,
    isFailed,
    elapsedTime: elapsedTimeRef.current,
    triggerPublish,
    reset,
  };
}
